import { isThisMonth } from 'date-fns/isThisMonth';
import bigDecimal from 'js-big-decimal';

import { format, getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { capitalize } from '@eika-infrastruktur/format';

import { currencyFormatter } from '../../components/shared/numberFormat';
import type {
    Account,
    AccountNumber,
    AccountOwner,
    Approver,
    Currency,
    PaymentItem,
    FuturePaymentPermissions,
    Initiator,
    PaymentItemDetails,
    PaymentItemStatus,
    PaymentLimits,
    PaymentSubStatus,
    PaymentType,
    TransactionActor,
} from '../../generated-types';

export interface FormattedFuturePayment {
    id: string;
    description?: string;
    date: Date;
    dateFormatted: string;
    amount: number;
    currency: Currency;
    amountFormatted: string;
    estimatedAmount?: number;
    balance?: string;
    notSufficientBalance: boolean;
    paymentType: PaymentType;
    paymentStatus: PaymentItemStatus | undefined;
    paymentSubStatus: PaymentSubStatus | undefined;
    fromAccount: { alias?: string; accountNumber: AccountNumber };
    toAccount: TransactionActor;
    permissions: FuturePaymentPermissions;
    owner?: AccountOwner;
    isFaked?: boolean;
    approvers: Approver[];
    initator?: Initiator;
}

export interface FormattedFuturePayments {
    id: string;
    label: string;
    futurePayments: FormattedFuturePayment[];
    totalAmount?: number;
}

const labelDateFormat = 'dd.MM';
const labelMonth = 'MMMM';
const labelYear = 'yyyy';

const getPreviousFuturePaymentDate = (futurePayments: ZonedFuturePayment[], index: number) => {
    const previous = index - 1;
    if (previous < 0) return null;
    return futurePayments[previous]?.dueDate;
};

const isNewMonth = ({ dueDate }: ZonedFuturePayment, futurePayments: ZonedFuturePayment[], index: number): boolean => {
    const prevDate = getPreviousFuturePaymentDate(futurePayments, index);
    return dueDate.getMonth() !== prevDate?.getMonth();
};

const isNewYear = ({ dueDate }: ZonedFuturePayment, futurePayments: ZonedFuturePayment[], index: number): boolean => {
    const prevDate = getPreviousFuturePaymentDate(futurePayments, index);
    return prevDate !== null && dueDate.getFullYear() !== prevDate.getFullYear();
};

const getMonthLabel = ({ dueDate }: ZonedFuturePayment) => {
    return capitalize(format(dueDate, labelMonth));
};

const getYearLabel = ({ dueDate }: ZonedFuturePayment) => {
    return format(dueDate, labelYear);
};

export const parseDate = (futurePayment: ZonedFuturePayment | PaymentItemDetails) =>
    ({
        ...futurePayment,
        dueDate: getZonedDate(futurePayment.dueDate),
    }) as ZonedFuturePayment;

const getPaymentLabel = (futurePayment: ZonedFuturePayment, allPayments: ZonedFuturePayment[], index: number) => {
    const monthLabel = getMonthLabel(futurePayment);
    const yearLabel = getYearLabel(futurePayment);
    if (isNewYear(futurePayment, allPayments, index)) {
        return monthLabel.concat(` ${yearLabel}`);
    }
    return monthLabel;
};

const getMonthId = (futurePayment: ZonedFuturePayment) => {
    const monthLabel = getMonthLabel(futurePayment);
    const yearLabel = getYearLabel(futurePayment);
    return `${monthLabel}${yearLabel}`;
};

export const addRunningBalance = (
    futurePayments: ZonedFuturePayment[],
    startingBalance: number | undefined,
): ZonedFuturePayment[] => {
    if (startingBalance === undefined) return futurePayments;
    return futurePayments.reduce((last, current) => {
        const prevBalance = last.length > 0 ? last[last.length - 1].availableAfterPayment : startingBalance;
        if (current.availableAfterPayment !== undefined) {
            return [...last, current];
        }
        return [
            ...last,
            {
                ...current,
                availableAfterPayment: parseFloat(
                    bigDecimal.add(prevBalance!, -(current.estimatedAmount ?? current.amount)),
                ),
            },
        ];
    }, [] as ZonedFuturePayment[]);
};

const localCurrencyFormat = (amount: number, currency: Currency = 'NOK') =>
    currencyFormatter(amount, currency, 2, undefined, false);

export const negateNumber = (amount: number) => parseFloat(bigDecimal.negate(amount));

export const getMonthlyTotalAmount = (futurePayments: FormattedFuturePayment[]) =>
    negateNumber(
        futurePayments.map((x) => x.estimatedAmount ?? x.amount).reduce((a, b) => parseFloat(bigDecimal.add(a, b)), 0),
    );

export const sumFuturePayments = (futurePayments: FormattedFuturePayment[], direction: 'IN' | 'OUT') => {
    const inOutCheck = (n: number) => (direction === 'OUT' ? n > 0 : n < 0);

    const sum = futurePayments
        .map((ffp) => ffp.estimatedAmount ?? ffp.amount)
        .reduce((accumulator, number) => (inOutCheck(number) ? accumulator + number : accumulator), 0);
    return Math.abs(sum);
};

const mapFromAccount = (futurePayment: ZonedFuturePayment, accounts: Account[] = []) => ({
    alias: accounts.find((x) => x.accountNumber.value === futurePayment.from?.accountNumber?.value)?.alias,
    accountNumber: futurePayment.from?.accountNumber ?? { value: '' }, // TODO: Can this actually be null?
});

interface ZonedFuturePayment extends Omit<PaymentItem, 'dueDate'> {
    dueDate: ZonedDate;
    isFaked?: boolean;
    availableAfterPayment: number;
}

export default function formatFuturePayments(
    futurePayments: PaymentItem[] | PaymentItemDetails[],
    startingBalance: number | undefined,
    totalAmount?: number,
    accounts?: Account[],
): FormattedFuturePayments[] {
    const sorted = futurePayments.map(parseDate).reverse();
    return addRunningBalance(sorted, startingBalance).reduce((map, futurePayment, index, allPayments) => {
        const formattedFuturePayment: FormattedFuturePayment = {
            id: futurePayment.paymentItemId.value,
            description: futurePayment.note,
            date: futurePayment.dueDate,
            dateFormatted: format(futurePayment.dueDate, labelDateFormat),
            amount: -futurePayment.amount,
            amountFormatted: localCurrencyFormat(-futurePayment.amount, futurePayment.currency),
            estimatedAmount: futurePayment.estimatedAmount,
            currency: futurePayment.currency,
            balance: futurePayment.availableAfterPayment
                ? localCurrencyFormat(futurePayment.availableAfterPayment)
                : undefined,
            notSufficientBalance:
                futurePayment?.availableAfterPayment != null ? futurePayment.availableAfterPayment < 0 : false,
            paymentType: futurePayment.paymentItemType!,
            paymentStatus: futurePayment.paymentItemStatus,
            paymentSubStatus: futurePayment.subStatus,
            fromAccount: mapFromAccount(futurePayment, accounts),
            toAccount: futurePayment.receiver,
            permissions: {
                canEdit: futurePayment?.permissions?.canEdit ?? false,
                canReject: futurePayment?.permissions?.canReject ?? false,
                canView: futurePayment?.permissions?.canView ?? false,
            },
            owner: accounts
                ?.find((x) => x.accountNumber.value === futurePayment.from?.accountNumber?.value)
                ?.owners?.find((x) => x.primaryOwner),
            isFaked: futurePayment.isFaked ?? false,
            approvers: futurePayment.approvers ?? [],
            initator: futurePayment.initiator,
        };

        if (isNewMonth(futurePayment, allPayments, index)) {
            const label = getPaymentLabel(futurePayment, allPayments, index);
            const id = getMonthId(futurePayment);
            const month = {
                id,
                label,
                futurePayments: [],
                totalAmount,
            };
            map.push(month);
        }

        map[map.length - 1].futurePayments.push(formattedFuturePayment);

        return map;
    }, [] as FormattedFuturePayments[]);
}

export const showPaymentLimitWarning =
    (formattedFuturePayments: FormattedFuturePayments[], paymentLimits?: PaymentLimits) =>
    (futurePayment: FormattedFuturePayments, enabled = true) => {
        if (!enabled) return false;
        if (formattedFuturePayments.length === 0) return false;
        if (!paymentLimits) return false;

        const totalAmount = getMonthlyTotalAmount(futurePayment.futurePayments);
        const isCurrentMonth = isThisMonth(futurePayment.futurePayments[0].date);
        if (isCurrentMonth) {
            return (
                (paymentLimits?.accumulatedMonthlyTransactionAmount ?? 0) >
                (paymentLimits?.maxMonthlyTransactionAmount ?? 0)
            );
        }

        return totalAmount > (paymentLimits?.maxMonthlyTransactionAmount ?? 0);
    };
